import React from 'react';
import PropTypes from 'prop-types';

import {Box, ButtonBase, Typography} from 'views/components';
import {KeyboardArrowDownRounded} from 'views/icons';
import {FilterMenuPopover, Translate, translate} from 'views/module';

import FilterDropdown from './dropdowns/FilterDropdown';
import Search from './search/Search';

import contrast from 'css/Contrast';

const FilterBar = (props) => {
  const {
    filterOptions,
    onClearFilter,
    selection,
    updateFilter,
  } = props;

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [openFilterKey, setOpenFilterKey] = React.useState(null);

  const statusFilter = filterOptions?.status;
  const certificationFilter = filterOptions?.certification;
  const languagesFilter = filterOptions?.languages;
  const locationFilter = filterOptions?.location;

  const statusOptions = React.useMemo(() => {
    return Object.values(statusFilter?.options ?? {}).sort((a, b) => a.rank - b.rank);
  }, [statusFilter]);
  const certificationOptions = React.useMemo(() => {
    return Object.values(certificationFilter?.options ?? {}).sort((a, b) => a.rank - b.rank);
  }, [certificationFilter]);
  const languagesOptions = React.useMemo(() => {
    return Object.values(languagesFilter?.options ?? {}).sort((a, b) => a.rank - b.rank);
  }, [languagesFilter]);

  const selectedStatusKeys = selection?.filter?.status?.optionSelected ?? [];
  const selectedCertificationKeys = selection?.filter?.certification?.optionSelected ?? [];
  const selectedLanguageKeys = selection?.filter?.languages?.optionSelected ?? [];
  const selectedMapValue = selection?.filter?.location?.optionSelected ?? 'disabled';
  const selectedSearchValue = selection?.filter?.caregiverName?.optionSelected ?? '';

  const handleOpenDropdown = (filterKey, event) => {
    if (openFilterKey === filterKey) {
      setAnchorEl(null);
      setOpenFilterKey(null);
      return;
    }

    setAnchorEl(event.currentTarget);
    setOpenFilterKey(filterKey);
  };

  const handleCloseDropdown = () => {
    setAnchorEl(null);
    setOpenFilterKey(null);
  };

  const handleToggleMultiSelect = (filter, option) => {
    updateFilter(filter, option);
  };

  const handleToggleMap = () => {
    updateFilter(locationFilter, locationFilter.options[selectedMapValue === 'enabled' ? 'disabled' : 'enabled']);
  };

  const isStatusDefault = selectedStatusKeys.length === 2 && selectedStatusKeys.includes('ACTIVE') && selectedStatusKeys.includes('PROFILE_REVIEW');
  const hasSearchFilter = Boolean(selectedSearchValue);
  const hasCertificationFilter = Boolean(selectedCertificationKeys.length);
  const hasLanguagesFilter = Boolean(selectedLanguageKeys.length);
  const isMapEnabled = selectedMapValue !== 'disabled';
  const hasNonDefaultStatusFilter = !isStatusDefault;
  const hasNonDefaultFiltersEnabled = hasSearchFilter || hasCertificationFilter || hasLanguagesFilter || isMapEnabled || hasNonDefaultStatusFilter;

  const renderDropdownButton = (filter, selectedKeys, selectedLabel, filterKey) => {
    const isSelected = Boolean(selectedKeys.length);
    const isOpen = openFilterKey === filterKey;

    return (
      <ButtonBase
        onClick={(event) => handleOpenDropdown(filterKey, event)}
        sx={{
          'display': 'flex',
          'alignItems': 'center',
          'justifyContent': 'center',
          'px': '9px',
          'py': '4px',
          'gap': '4px',
          'borderRadius': '20px',
          'border': `1px solid ${isSelected || isOpen ? '#EBBDAD' : '#E8E4DE'}`,
          'backgroundColor': isSelected || isOpen ? '#FFF7F4' : '#F9F8F6',
          'whiteSpace': 'nowrap',
          'transition': 'box-shadow 0.2s ease, background-color 0.2s ease, border-color 0.2s ease',
          '&:hover': {
            boxShadow: '0px 0px 0px 3px rgba(218, 121, 85, 0.12)',
          },
        }}
      >
        <Typography
          sx={{
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '14px',
            color: isSelected || isOpen ? '#DA7955' : '#6B6760',
          }}
        >
          {selectedLabel || filter.label}
        </Typography>
        <KeyboardArrowDownRounded
          sx={{
            fontSize: '16px',
            color: isSelected || isOpen ? '#DA7955' : '#6B6760',
          }}
        />
      </ButtonBase>
    );
  };

  if (!statusFilter || !certificationFilter || !languagesFilter || !locationFilter) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          alignContent: 'flex-start',
          width: '100%',
          gap: '12px 8px',
        }}
      >
        <Search
          selection={selection}
          filterOptions={filterOptions}
          updateFilter={updateFilter}
        />
        <Box
          sx={{
            width: '1px',
            minWidth: '1px',
            height: '25px',
            backgroundColor: '#E8E4DE',
          }}
        />
        {
          statusOptions.map((option) => {
            const isSelected = selectedStatusKeys.includes(option.key);
            const backgroundColor = isSelected ? translate(`caregiver.Status.${option.key}.Color`) : '#F9F8F6';
            const borderColor = isSelected ? backgroundColor : '#E8E4DE';
            const textColor = isSelected ? contrast(backgroundColor) : '#6B6760';

            return (
              <ButtonBase
                key={option.key}
                onClick={() => updateFilter(statusFilter, option)}
                sx={{
                  'display': 'flex',
                  'alignItems': 'center',
                  'justifyContent': 'center',
                  'px': '9px',
                  'py': '4px',
                  'borderRadius': '20px',
                  'border': `1px solid ${borderColor}`,
                  'backgroundColor': backgroundColor,
                  'whiteSpace': 'nowrap',
                  'transition': 'box-shadow 0.2s ease, background-color 0.2s ease, border-color 0.2s ease',
                  '&:hover': {
                    boxShadow: '0px 0px 0px 3px rgba(218, 121, 85, 0.12)',
                  },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Geist Mono',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '14px',
                    color: textColor,
                  }}
                >
                  {option.label}
                </Typography>
              </ButtonBase>
            );
          })
        }
        <Box
          sx={{
            width: '1px',
            minWidth: '1px',
            height: '25px',
            backgroundColor: '#E8E4DE',
          }}
        />
        {renderDropdownButton(certificationFilter, selectedCertificationKeys, certificationFilter.label, 'certification')}
        {renderDropdownButton(languagesFilter, selectedLanguageKeys, languagesFilter.label, 'languages')}
        <ButtonBase
          onClick={handleToggleMap}
          sx={{
            'display': 'flex',
            'alignItems': 'center',
            'justifyContent': 'center',
            'px': '9px',
            'py': '4px',
            'borderRadius': '20px',
            'border': `1px solid ${selectedMapValue === 'enabled' ? '#EBBDAD' : '#E8E4DE'}`,
            'backgroundColor': selectedMapValue === 'enabled' ? '#FFF7F4' : '#F9F8F6',
            'whiteSpace': 'nowrap',
            'transition': 'box-shadow 0.2s ease, background-color 0.2s ease, border-color 0.2s ease',
            '&:hover': {
              boxShadow: '0px 0px 0px 3px rgba(218, 121, 85, 0.12)',
            },
          }}
        >
          <Typography
            sx={{
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '14px',
              color: selectedMapValue === 'enabled' ? '#DA7955' : '#6B6760',
            }}
          >
            {
              selectedMapValue === 'enabled' ?
              translate('caregiver.CaregiverList.Filter.MapEnabled') :
              translate('caregiver.CaregiverList.Filter.MapDisabled')
            }
          </Typography>
        </ButtonBase>
        <ButtonBase
          onClick={onClearFilter}
          sx={{
            'display': 'flex',
            'alignItems': 'center',
            'justifyContent': 'center',
            'px': '9px',
            'py': '4px',
            'borderRadius': '20px',
            'border': '1px solid #E8E4DE',
            'backgroundColor': hasNonDefaultFiltersEnabled ? '#FFF7F4' : '#F9F8F6',
            'whiteSpace': 'nowrap',
            'transition': 'box-shadow 0.2s ease, background-color 0.2s ease, border-color 0.2s ease',
            '&:hover': {
              boxShadow: '0px 0px 0px 3px rgba(218, 121, 85, 0.12)',
            },
          }}
        >
          <Typography
            sx={{
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '14px',
              color: hasNonDefaultFiltersEnabled ? '#DA7955' : '#6B6760',
            }}
          >
            <Translate expression='generic.ClearAllFilters' />
          </Typography>
        </ButtonBase>
      </Box>
      {
        anchorEl && openFilterKey === 'certification' &&
        <FilterMenuPopover
          anchorEl={anchorEl}
          anchorPositionTop={anchorEl.getBoundingClientRect().bottom + 8}
          anchorPositionLeft={anchorEl.getBoundingClientRect().left}
          onClose={handleCloseDropdown}
          paperSx={{
            boxShadow: '0px 4px 12.1px 0px #0000000D',
          }}
          display={
            <FilterDropdown
              multiSelect
              title={certificationFilter.label}
              options={certificationOptions}
              selectedKeys={selectedCertificationKeys}
              onToggleOption={(option) => handleToggleMultiSelect(certificationFilter, option)}
            />
          }
        />
      }
      {
        anchorEl && openFilterKey === 'languages' &&
        <FilterMenuPopover
          anchorEl={anchorEl}
          anchorPositionTop={anchorEl.getBoundingClientRect().bottom + 8}
          anchorPositionLeft={anchorEl.getBoundingClientRect().left}
          onClose={handleCloseDropdown}
          paperSx={{
            boxShadow: '0px 4px 12.1px 0px #0000000D',
          }}
          display={
            <FilterDropdown
              multiSelect
              title={languagesFilter.label}
              options={languagesOptions}
              selectedKeys={selectedLanguageKeys}
              onToggleOption={(option) => handleToggleMultiSelect(languagesFilter, option)}
              width='235px'
            />
          }
        />
      }
    </Box>
  );
};

FilterBar.propTypes = {
  filterOptions: PropTypes.object.isRequired,
  onClearFilter: PropTypes.func.isRequired,
  selection: PropTypes.object.isRequired,
  updateFilter: PropTypes.func.isRequired,
};

export default React.memo(FilterBar);
